import { useState, useEffect } from "react";
import "../../style/scss/Recruit.scss";
import {
  Form,
  FormActions,
  FormInput,
  FormSelect,
  FormDate,
  FormTextArea,
} from "../Controls/Form";
import Helpers from "../Controls/helpers";
import UserData from "../Controls/UserData";
import JobPackageSelectionListItem from "../ListItems/JobPackageSelectionListItem";
import SkillSearch from "../Controls/SkillSearch";
import SkillEditModal from "../Modals/SkillEditModal";
import JobMatchReview from "../Views/JobMatchReview";
import CustomButton from "../Controls/CustomButton";
import ListIconButton from "../Controls/ListIconButton";
import API from "@src/API.js";
import { toast } from "react-toastify";
import { FaTrashAlt } from "react-icons/fa";

function formatLocationDisplay(loc) {
  if (loc?.DisplayName || loc?.displayName) return loc.DisplayName || loc.displayName;
  const parts = [loc?.CityName || loc?.cityName, loc?.StateName || loc?.stateName, loc?.CountryName || loc?.countryName]
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "Location";
}

/**
 * Job posting form body. Prefer JobPostingEditModal for UI entry points.
 */
export default function JobPostingEdit({
  jobPostingId = 0,
  employerId = 0,
  employerName = "",
  roleId = 0,
  roles: rolesProp = [],
  packages: packagesProp = [],
  onClose,
}) {
  const id = Number.parseInt(jobPostingId, 10) || 0;
  const isCreate = id <= 0;
  const empId = Number.parseInt(employerId, 10) || 0;
  const initialRoleId = Number.parseInt(roleId, 10) || 0;

  const seedPackages = (list) =>
    (list || []).map((p) => ({
      ID: p.ID || p.id,
      Name: p.Name || p.name,
      Selected: !!(p.Selected ?? p.selected),
    }));

  const [lookups] = useState(UserData.Lookups() || {});
  const [job, updateJob] = useState({
    ID: isCreate ? 0 : id,
    EmployerID: empId,
    EmployerRoleID: initialRoleId,
    EmployerName: employerName || "",
    Title: "",
    JobDescription: "",
    MaxHiringCompensation: 0,
    MaxHiringBonus: 0,
    TravelPercent: 0,
    WfhPercent: 0,
    OnSite: false,
    OfficeDaysPerWeek: null,
    PositionCount: 1,
    Packages: isCreate ? seedPackages(packagesProp) : [],
    Locations: [],
    AvailableEmployerLocations: [],
    Roles: [],
    Skills: [],
  });
  const [roles] = useState(() =>
    (rolesProp || []).map((r) => ({
      ...r,
      id: r.ID ?? r.id,
      title: r.Title ?? r.title,
    }))
  );
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("description");
  const [hydrated, setHydrated] = useState(isCreate);
  const [editSkillId, setEditSkillId] = useState(null);

  const [customDraft, setCustomDraft] = useState({
    CountryID: null,
    CountryName: "",
    StateID: null,
    StateName: "",
    CityID: null,
    CityName: "",
  });
  const [draftStates, setDraftStates] = useState([]);
  const [draftCities, setDraftCities] = useState([]);
  const [employerSiteId, setEmployerSiteId] = useState("");

  useEffect(() => {
    let cancelled = false;
    setHydrated(isCreate);
    (async () => {
      const url = isCreate
        ? `/JobPosting?id=0&employerId=${empId}`
        : `/JobPosting?id=${id}`;
      if (isCreate && !empId) {
        updateJob((prev) => ({
          ...prev,
          EmployerID: empId || prev.EmployerID,
          EmployerRoleID: initialRoleId || prev.EmployerRoleID,
          EmployerName: employerName || prev.EmployerName,
          Packages:
            (prev.Packages || []).length > 0
              ? prev.Packages
              : seedPackages(packagesProp),
        }));
        if (!cancelled) setHydrated(true);
        return;
      }

      const resp = await API.getAsync(url);
      if (cancelled) return;
      if (resp?.ok && resp.jsonData) {
        const data = resp.jsonData;
        updateJob((prev) => ({
          ...prev,
          ...data,
          ID: isCreate ? 0 : data.ID ?? data.id ?? id,
          Title: data.Title ?? data.title ?? prev.Title ?? "",
          JobDescription: data.JobDescription ?? data.jobDescription ?? prev.JobDescription ?? "",
          EmployerID: data.EmployerID ?? data.employerID ?? empId ?? prev.EmployerID,
          EmployerRoleID:
            initialRoleId || data.EmployerRoleID || data.employerRoleID || prev.EmployerRoleID,
          EmployerName:
            employerName || data.EmployerName || data.employerName || prev.EmployerName,
          EmbargoDate: data.EmbargoDate ?? data.embargoDate ?? null,
          KillDate: data.KillDate ?? data.killDate ?? null,
          StartWorkDate: data.StartWorkDate ?? data.startWorkDate ?? null,
          MaxHiringCompensation:
            data.MaxHiringCompensation ?? data.maxHiringCompensation ?? prev.MaxHiringCompensation,
          MaxHiringBonus: data.MaxHiringBonus ?? data.maxHiringBonus ?? prev.MaxHiringBonus,
          TravelPercent: data.TravelPercent ?? data.travelPercent ?? prev.TravelPercent,
          WfhPercent: data.WfhPercent ?? data.wfhPercent ?? prev.WfhPercent,
          OnSite: data.OnSite ?? data.onSite ?? prev.OnSite,
          PositionCount: data.PositionCount ?? data.positionCount ?? prev.PositionCount,
          Skills: data.Skills || data.skills || [],
          Packages:
            data.Packages || data.packages || seedPackages(packagesProp) || [],
          Locations: data.Locations || data.locations || [],
          AvailableEmployerLocations:
            data.AvailableEmployerLocations || data.availableEmployerLocations || [],
          OfficeDaysPerWeek: data.OfficeDaysPerWeek ?? data.officeDaysPerWeek ?? null,
        }));
        setHydrated(true);
      } else if (!isCreate) {
        toast.error("Unable to load job posting");
        setHydrated(true);
      } else {
        updateJob((prev) => ({
          ...prev,
          EmployerID: empId || prev.EmployerID,
          EmployerRoleID: initialRoleId || prev.EmployerRoleID,
          EmployerName: employerName || prev.EmployerName,
          Packages:
            (prev.Packages || []).length > 0
              ? prev.Packages
              : seedPackages(packagesProp),
        }));
        setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate, empId, initialRoleId, employerName, packagesProp?.length]);

  function close() {
    onClose?.();
  }

  function setOnSiteMode(onSite) {
    if (onSite) {
      updateJob({ ...job, OnSite: true });
      return;
    }
    updateJob({
      ...job,
      OnSite: false,
      OfficeDaysPerWeek: null,
      WfhPercent: null,
      Locations: [],
    });
    setCustomDraft({
      CountryID: null,
      CountryName: "",
      StateID: null,
      StateName: "",
      CityID: null,
      CityName: "",
    });
    setDraftStates([]);
    setDraftCities([]);
    setEmployerSiteId("");
  }

  async function onDoneAsync() {
    if (busy) return;
    setBusy(true);
    try {
      const onSite = !!(job.OnSite ?? job.onSite);
      const payload = {
        ...job,
        ID: job.ID || 0,
        EmployerID: job.EmployerID || empId || 0,
        Title: job.Title || job.title || "",
        JobDescription: job.JobDescription || job.jobDescription || "",
        MaxHiringCompensation: Number(job.MaxHiringCompensation ?? job.maxHiringCompensation ?? 0),
        MaxHiringBonus: Number(job.MaxHiringBonus ?? job.maxHiringBonus ?? 0),
        TravelPercent: Number(job.TravelPercent ?? job.travelPercent ?? 0),
        WfhPercent: onSite ? Number(job.WfhPercent ?? job.wfhPercent ?? 0) : null,
        OnSite: onSite,
        OfficeDaysPerWeek: onSite
          ? Number(job.OfficeDaysPerWeek ?? job.officeDaysPerWeek ?? 0)
          : null,
        PositionCount: Number(job.PositionCount ?? job.positionCount ?? 1),
        Locations: onSite
          ? (job.Locations || []).map((loc) => ({
              ID: loc.ID || loc.id || 0,
              EmployerLocationID: loc.EmployerLocationID || loc.employerLocationID || null,
              CountryID: loc.CountryID || loc.countryID || null,
              StateID: loc.StateID || loc.stateID || null,
              CityID: loc.CityID || loc.cityID || null,
              CountryName: loc.CountryName || loc.countryName,
              StateName: loc.StateName || loc.stateName,
              CityName: loc.CityName || loc.cityName,
              Source: loc.Source || loc.source,
              DisplayName: loc.DisplayName || loc.displayName,
            }))
          : [],
        Skills: (job.Skills || []).map((s) => ({
          SkillID: s.SkillID || s.skillID,
          SkillName: s.SkillName || s.skillName,
          Level: s.Level || s.level || 3,
          Required: !!(s.Required ?? s.required ?? s.MustHave),
        })),
        Packages: (job.Packages || []).map((p) => ({
          ID: p.ID || p.id,
          Name: p.Name || p.name,
          Selected: !!(p.Selected ?? p.selected),
        })),
      };

      const resp = await API.postAsync("/PostJobPosting", payload);
      if (resp?.ok) {
        toast.success("Job posting saved");
        close();
      } else {
        toast.error(resp?.jsonData?.message || "Failed to save job posting");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteAsync() {
    if (!(job.ID > 0)) {
      close();
      return;
    }
    const resp = await API.postAsync("/DeleteObj", { ObjectType: "Job", ID: job.ID });
    if (resp?.ok) {
      toast.success("Job posting deleted");
      close();
    } else {
      toast.error("Failed to delete job posting");
    }
  }

  const onChangeRole = (roleID) => {
    const id = Number(roleID?.ID ?? roleID?.id ?? roleID) || 0;
    const newRole = roles.find((r) => Number(r.id || r.ID) === id);
    if (!newRole) {
      updateJob({ ...job, EmployerRoleID: id || roleID });
      return;
    }
    updateJob({
      ...job,
      EmployerRoleID: id,
      Title: newRole.Title || newRole.title || job.Title,
      TravelPercent: newRole.TravelPercentage ?? newRole.travelPercentage ?? job.TravelPercent,
      WfhPercent: newRole.WfhPercentage ?? newRole.wfhPercentage ?? job.WfhPercent,
      OnSite: newRole.OnSiteRequired ?? newRole.onSiteRequired ?? job.OnSite,
      EmploymentTypeID: newRole.EmploymentTypeID ?? job.EmploymentTypeID,
      Skills:
        (newRole.Skills || []).map((s) => ({
          SkillID: s.SkillID,
          SkillName: s.SkillName,
          Level: s.Level || 3,
          Required: !!(s.Required ?? s.MustHave),
        })) || job.Skills,
    });
  };

  const togglePackage = (pkgId) => {
    const list = (job.Packages || []).map((p) => {
      const pid = p.ID || p.id;
      if (pid === pkgId) return { ...p, Selected: !p.Selected, selected: !p.Selected };
      return p;
    });
    updateJob({ ...job, Packages: list });
  };

  const onSkillSelect = (skill) => {
    if (!skill || typeof skill === "string") return;
    const skillId = skill.id || skill.ID;
    if (!skillId) return;
    if ((job.Skills || []).some((s) => (s.SkillID || s.skillID) === skillId)) return;
    updateJob({
      ...job,
      Skills: [
        ...(job.Skills || []),
        {
          SkillID: skillId,
          SkillName: skill.shortName || skill.Name || skill.name,
          Level: 3,
          Required: false,
        },
      ],
    });
  };

  const toggleMustHave = (skillId) => {
    updateJob({
      ...job,
      Skills: (job.Skills || []).map((s) =>
        (s.SkillID || s.skillID) === skillId
          ? { ...s, Required: !s.Required, required: !s.Required }
          : s
      ),
    });
  };

  const removeSkill = (skillId) => {
    updateJob({
      ...job,
      Skills: (job.Skills || []).filter((s) => (s.SkillID || s.skillID) !== skillId),
    });
  };

  const removeLocation = (index) => {
    const next = [...(job.Locations || [])];
    next.splice(index, 1);
    updateJob({ ...job, Locations: next });
  };

  const addEmployerSite = () => {
    const siteId = Number.parseInt(employerSiteId, 10);
    if (!siteId) return;
    const sites = job.AvailableEmployerLocations || [];
    const site = sites.find((s) => (s.ID || s.id) === siteId);
    if (!site) return;
    if ((job.Locations || []).some((l) => (l.EmployerLocationID || l.employerLocationID) === siteId)) {
      toast.info("That employer site is already added");
      return;
    }
    updateJob({
      ...job,
      Locations: [
        ...(job.Locations || []),
        {
          EmployerLocationID: siteId,
          CountryID: site.CountryID || site.countryID,
          CountryName: site.CountryName || site.countryName,
          StateID: site.StateID || site.stateID,
          StateName: site.StateName || site.stateName,
          CityID: site.CityID || site.cityID,
          CityName: site.CityName || site.cityName,
          Source: "Employer",
          DisplayName: site.Name || site.name,
        },
      ],
    });
    setEmployerSiteId("");
  };

  const onDraftCountry = async (countryId) => {
    const idNum = Number(countryId) || null;
    const country = (lookups.countries || []).find((c) => c.Key === idNum);
    const nextStates = idNum ? (await Helpers.StatesAsync(idNum)) || [] : [];
    setCustomDraft({
      CountryID: idNum,
      CountryName: country?.Value || "",
      StateID: null,
      StateName: "",
      CityID: null,
      CityName: "",
    });
    setDraftStates(nextStates);
    setDraftCities([]);
  };

  const onDraftState = async (stateId) => {
    const idNum = Number(stateId) || null;
    const state = draftStates.find((s) => (s.ID || s.id) === idNum);
    const nextCities = idNum ? (await Helpers.CitiesAsync(idNum)) || [] : [];
    setCustomDraft((prev) => ({
      ...prev,
      StateID: idNum,
      StateName: state?.Name || state?.name || "",
      CityID: null,
      CityName: "",
    }));
    setDraftCities(nextCities);
  };

  const onDraftCity = (cityId) => {
    const idNum = Number(cityId) || null;
    const city = draftCities.find((c) => (c.ID || c.id) === idNum);
    setCustomDraft((prev) => ({
      ...prev,
      CityID: idNum,
      CityName: city?.Name || city?.name || "",
    }));
  };

  const addCustomLocation = () => {
    if (!customDraft.CountryID) {
      toast.error("Select a country for the custom location");
      return;
    }
    const key = `${customDraft.CountryID}:${customDraft.StateID || 0}:${customDraft.CityID || 0}`;
    const exists = (job.Locations || []).some((l) => {
      if (l.EmployerLocationID || l.employerLocationID) return false;
      const k = `${l.CountryID || l.countryID || 0}:${l.StateID || l.stateID || 0}:${l.CityID || l.cityID || 0}`;
      return k === key;
    });
    if (exists) {
      toast.info("That location is already added");
      return;
    }
    updateJob({
      ...job,
      Locations: [
        ...(job.Locations || []),
        {
          EmployerLocationID: null,
          CountryID: customDraft.CountryID,
          CountryName: customDraft.CountryName,
          StateID: customDraft.StateID,
          StateName: customDraft.StateName,
          CityID: customDraft.CityID,
          CityName: customDraft.CityName,
          Source: "Custom",
          DisplayName: formatLocationDisplay(customDraft),
        },
      ],
    });
    setCustomDraft({
      CountryID: null,
      CountryName: "",
      StateID: null,
      StateName: "",
      CityID: null,
      CityName: "",
    });
    setDraftStates([]);
    setDraftCities([]);
  };

  const skills = job.Skills || [];
  const packages = job.Packages || [];
  const locations = job.Locations || [];
  const availableSites = job.AvailableEmployerLocations || [];
  const selectedPackageCount = packages.filter((p) => p.Selected ?? p.selected).length;
  const onSite = !!(job.OnSite ?? job.onSite);
  const unusedSites = availableSites.filter(
    (s) => !locations.some((l) => (l.EmployerLocationID || l.employerLocationID) === (s.ID || s.id))
  );

  return (
    <div className="cm-modal-embedded">
      <Form
        key={hydrated ? `job-posting-${id}-ready` : `job-posting-${id}-loading`}
        className="cm-modal-form"
        object={job}
        onUpdate={updateJob}
      >
        <div className="cm-modal-tabs" role="tablist" aria-label="Job posting sections">
          <button
            type="button"
            role="tab"
            id="job-posting-tab-description"
            aria-selected={tab === "description"}
            aria-controls="job-posting-panel-description"
            className={tab === "description" ? "is-active" : undefined}
            data-testid="job-posting-tab-description"
            onClick={() => setTab("description")}
          >
            Description
          </button>
          <button
            type="button"
            role="tab"
            id="job-posting-tab-parameters"
            aria-selected={tab === "parameters"}
            aria-controls="job-posting-panel-parameters"
            className={tab === "parameters" ? "is-active" : undefined}
            data-testid="job-posting-tab-parameters"
            onClick={() => setTab("parameters")}
          >
            Parameters
          </button>
          <button
            type="button"
            role="tab"
            id="job-posting-tab-location"
            aria-selected={tab === "location"}
            aria-controls="job-posting-panel-location"
            className={tab === "location" ? "is-active" : undefined}
            data-testid="job-posting-tab-location"
            onClick={() => setTab("location")}
          >
            Location{onSite && locations.length > 0 ? ` (${locations.length})` : ""}
          </button>
          <button
            type="button"
            role="tab"
            id="job-posting-tab-skills"
            aria-selected={tab === "skills"}
            aria-controls="job-posting-panel-skills"
            className={tab === "skills" ? "is-active" : undefined}
            data-testid="job-posting-tab-skills"
            onClick={() => setTab("skills")}
          >
            Skills{skills.length > 0 ? ` (${skills.length})` : ""}
          </button>
          <button
            type="button"
            role="tab"
            id="job-posting-tab-packages"
            aria-selected={tab === "packages"}
            aria-controls="job-posting-panel-packages"
            className={tab === "packages" ? "is-active" : undefined}
            data-testid="job-posting-tab-packages"
            onClick={() => setTab("packages")}
          >
            Packages{selectedPackageCount > 0 ? ` (${selectedPackageCount})` : ""}
          </button>
          <button
            type="button"
            role="tab"
            id="job-posting-tab-matches"
            aria-selected={tab === "matches"}
            aria-controls="job-posting-panel-matches"
            className={tab === "matches" ? "is-active" : undefined}
            data-testid="job-posting-tab-matches"
            onClick={() => setTab("matches")}
          >
            Matches
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="job-posting-panel-description"
            role="tabpanel"
            aria-labelledby="job-posting-tab-description"
            aria-hidden={tab !== "description"}
            className={`cm-modal-tab-panel${tab === "description" ? " is-active" : ""}`}
            {...(tab !== "description" ? { inert: "" } : {})}
          >
            {roles.length > 0 ? (
              <FormSelect
                field="EmployerRoleID"
                label="Role"
                options={roles}
                valueProp="id"
                labelProp="title"
                nameField="title"
                onChange={onChangeRole}
              />
            ) : null}
            <FormInput field="Title" label="Title" required />
            <FormTextArea field="JobDescription" label="Description" />
          </div>

          <div
            id="job-posting-panel-parameters"
            role="tabpanel"
            aria-labelledby="job-posting-tab-parameters"
            aria-hidden={tab !== "parameters"}
            className={`cm-modal-tab-panel${tab === "parameters" ? " is-active" : ""}`}
            {...(tab !== "parameters" ? { inert: "" } : {})}
          >
            <FormDate field="EmbargoDate" label="Campaign Start Date" />
            <FormDate field="KillDate" label="Campaign End Date" />
            <FormDate field="StartWorkDate" label="Start Work Date" />
            <FormInput field="PositionCount" type="number" label="Number of positions to fill" />
            <FormInput field="MaxHiringCompensation" type="number" label="Max annual compensation" />
            <FormInput field="MaxHiringBonus" type="number" label="Max hiring bonus" />
          </div>

          <div
            id="job-posting-panel-location"
            role="tabpanel"
            aria-labelledby="job-posting-tab-location"
            aria-hidden={tab !== "location"}
            className={`cm-modal-tab-panel${tab === "location" ? " is-active" : ""}`}
            {...(tab !== "location" ? { inert: "" } : {})}
          >
            <div className="job-posting-work-mode" role="group" aria-label="Work location mode">
              <button
                type="button"
                className={onSite ? "is-active" : undefined}
                data-testid="job-posting-mode-onsite"
                onClick={() => setOnSiteMode(true)}
              >
                Onsite
              </button>
              <button
                type="button"
                className={!onSite ? "is-active" : undefined}
                data-testid="job-posting-mode-remote"
                onClick={() => setOnSiteMode(false)}
              >
                Remote
              </button>
            </div>

            {!onSite ? (
              <p className="fieldHint">Remote postings do not require office locations.</p>
            ) : (
              <>
                <FormInput
                  field="OfficeDaysPerWeek"
                  type="number"
                  label="Days per week in office"
                  min={0}
                  max={7}
                />
                <FormInput field="WfhPercent" type="number" label="WFH percent" />
                <FormInput field="TravelPercent" type="number" label="Travel percent" />

                {locations.length > 0 ? (
                  <ul className="job-posting-location-list">
                    {locations.map((loc, index) => (
                      <li key={`${loc.ID || loc.id || "new"}-${index}`} className="job-posting-location-item">
                        <span className="job-posting-location-item__name">
                          {formatLocationDisplay(loc)}
                          <span className="job-posting-location-item__source">
                            {(loc.Source || loc.source) === "Employer" ? "Employer site" : "Custom"}
                          </span>
                        </span>
                        <ListIconButton
                          aria-label="Remove location"
                          title="Remove location"
                          onClick={() => removeLocation(index)}
                        >
                          <FaTrashAlt aria-hidden />
                        </ListIconButton>
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="job-posting-location-add">
                  <div className="job-posting-location-add__row">
                    <label className="job-posting-location-add__label" htmlFor="job-employer-site">
                      Employer site
                    </label>
                    <select
                      id="job-employer-site"
                      data-testid="job-posting-employer-site"
                      value={employerSiteId}
                      onChange={(e) => setEmployerSiteId(e.target.value)}
                      disabled={unusedSites.length === 0}
                    >
                      <option value="">
                        {unusedSites.length === 0 ? "No employer sites available" : "Select a site…"}
                      </option>
                      {unusedSites.map((s) => (
                        <option key={s.ID || s.id} value={s.ID || s.id}>
                          {s.Name || s.name}
                        </option>
                      ))}
                    </select>
                    <CustomButton
                      type="button"
                      variant="contained"
                      color="primary"
                      data-testid="job-posting-add-employer-site"
                      disabled={!employerSiteId}
                      onClick={addEmployerSite}
                    >
                      Add site
                    </CustomButton>
                  </div>

                  <div className="job-posting-location-add__custom">
                    <p className="job-posting-location-add__label">Custom location</p>
                    <div className="job-posting-location-add__row">
                      <select
                        data-testid="job-posting-custom-country"
                        value={customDraft.CountryID || ""}
                        onChange={(e) => onDraftCountry(e.target.value)}
                      >
                        <option value="">Country…</option>
                        {(lookups.countries || []).map((c) => (
                          <option key={c.Key} value={c.Key}>
                            {c.Value}
                          </option>
                        ))}
                      </select>
                      <select
                        data-testid="job-posting-custom-state"
                        value={customDraft.StateID || ""}
                        onChange={(e) => onDraftState(e.target.value)}
                        disabled={!customDraft.CountryID}
                      >
                        <option value="">State…</option>
                        {draftStates.map((s) => (
                          <option key={s.ID || s.id} value={s.ID || s.id}>
                            {s.Name || s.name}
                          </option>
                        ))}
                      </select>
                      <select
                        data-testid="job-posting-custom-city"
                        value={customDraft.CityID || ""}
                        onChange={(e) => onDraftCity(e.target.value)}
                        disabled={!customDraft.StateID}
                      >
                        <option value="">City…</option>
                        {draftCities.map((c) => (
                          <option key={c.ID || c.id} value={c.ID || c.id}>
                            {c.Name || c.name}
                          </option>
                        ))}
                      </select>
                      <CustomButton
                        type="button"
                        variant="contained"
                        color="primary"
                        data-testid="job-posting-add-custom-location"
                        onClick={addCustomLocation}
                      >
                        Add location
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div
            id="job-posting-panel-skills"
            role="tabpanel"
            aria-labelledby="job-posting-tab-skills"
            aria-hidden={tab !== "skills"}
            className={`cm-modal-tab-panel${tab === "skills" ? " is-active" : ""}`}
            {...(tab !== "skills" ? { inert: "" } : {})}
          >
            <p className="fieldHint">Add skills required for this posting. Mark must-haves as needed.</p>
            <div className="field form-group">
              <SkillSearch name="Skill" label="Add skill" onSelect={onSkillSelect} />
            </div>
            {skills.length === 0 ? (
              <p className="fieldHint">No skills added yet.</p>
            ) : (
              <ul className="job-posting-skill-list">
                {skills.map((s) => {
                  const sid = s.SkillID || s.skillID;
                  const name = s.SkillName || s.skillName || "Skill";
                  return (
                    <li key={sid} className="job-posting-skill-item">
                      <button
                        type="button"
                        className="job-posting-skill-item__name"
                        data-testid={`job-posting-skill-edit-${sid}`}
                        onClick={() => sid > 0 && setEditSkillId(sid)}
                      >
                        {name}
                      </button>
                      <label className="job-posting-skill-item__must">
                        <input
                          type="checkbox"
                          checked={!!(s.Required ?? s.required)}
                          onChange={() => toggleMustHave(sid)}
                        />{" "}
                        Must-have
                      </label>
                      <ListIconButton
                        aria-label={`Remove ${name}`}
                        title="Remove skill"
                        onClick={() => removeSkill(sid)}
                      >
                        <FaTrashAlt aria-hidden />
                      </ListIconButton>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            id="job-posting-panel-packages"
            role="tabpanel"
            aria-labelledby="job-posting-tab-packages"
            aria-hidden={tab !== "packages"}
            className={`cm-modal-tab-panel${tab === "packages" ? " is-active" : ""}`}
            {...(tab !== "packages" ? { inert: "" } : {})}
          >
            <p className="fieldHint">Select which benefit packages apply to this job posting.</p>
            {packages.length === 0 ? (
              <p className="fieldHint">No packages available for this employer.</p>
            ) : (
              <ul className="job-posting-package-list">
                {packages.map((p) => {
                  const pid = p.ID || p.id;
                  return (
                    <li key={pid}>
                      <JobPackageSelectionListItem
                        id={pid}
                        name={p.Name || p.name}
                        selected={!!(p.Selected ?? p.selected)}
                        onClick={togglePackage}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            id="job-posting-panel-matches"
            role="tabpanel"
            aria-labelledby="job-posting-tab-matches"
            aria-hidden={tab !== "matches"}
            className={`cm-modal-tab-panel${tab === "matches" ? " is-active" : ""}`}
            {...(tab !== "matches" ? { inert: "" } : {})}
          >
            {job.ID > 0 ? (
              <JobMatchReview
                key={`matches-${job.ID}`}
                jobId={job.ID}
                embedded
                jobTitle={job.Title || job.title}
                employerId={job.EmployerID || empId}
              />
            ) : (
              <p className="fieldHint">Save this job posting first to review system-ranked matches.</p>
            )}
          </div>
        </div>

        <FormActions
          doneLabel={busy ? "Saving…" : "Save"}
          onSaveAsync={async () => {
            await onDoneAsync();
          }}
          onCancelAsync={async () => {
            close();
          }}
          onDeleteAsync={
            job.ID > 0
              ? async () => {
                  await onDeleteAsync();
                }
              : undefined
          }
        />
      </Form>

      {editSkillId != null ? (
        <SkillEditModal
          skillId={editSkillId}
          onClose={(saved) => {
            setEditSkillId(null);
            if (!saved) return;
            const savedId = saved.ID ?? saved.id;
            const savedName = saved.Name ?? saved.name ?? saved.ShortName ?? saved.shortName;
            if (!savedId || !savedName) return;
            updateJob((prev) => ({
              ...prev,
              Skills: (prev.Skills || []).map((s) =>
                (s.SkillID || s.skillID) === savedId
                  ? { ...s, SkillName: savedName, skillName: savedName }
                  : s
              ),
            }));
          }}
        />
      ) : null}
    </div>
  );
}
